import { forwardRef } from "react";
import SpinnerComponent from "../SpinnerComponent";

const base = [
  "tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5",
  "tw-font-semibold tw-cursor-pointer tw-transition-all tw-ease-[ease]",
  "focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2",
  "disabled:tw-opacity-65 disabled:tw-cursor-not-allowed",
].join(" ");

const RESPONSIVE_PRIMARY = [
  "max-md:tw-px-[10px] max-md:tw-my-auto max-md:tw-mx-0",
  "max-sm:tw-w-full max-sm:tw-justify-center",
  "md:max-[1024px]:tw-px-4 md:max-[1024px]:tw-mx-[2px] md:max-[1024px]:tw-my-auto",
].join(" ");

const RESPONSIVE_SECONDARY = [
  "max-md:tw-w-full max-md:tw-max-w-full max-md:tw-min-w-[101px]",
  "max-md:tw-px-[10px] max-md:tw-mt-0 max-md:tw-mx-0 max-md:tw-mb-[10px]",
  "md:max-[1024px]:tw-px-[10px] md:max-[1024px]:tw-mt-0 md:max-[1024px]:tw-mx-[5px] md:max-[1024px]:tw-mb-2",
].join(" ");

const variants = {
  primary: [
    "tw-duration-slow tw-border-0",
    "tw-bg-brand-red tw-text-white",
    "enabled:hover:tw-bg-brand-redHover",
    "focus-visible:tw-ring-brand-red",
    RESPONSIVE_PRIMARY,
  ].join(" "),

  secondary: [
    "tw-duration-slow tw-border-[1.5px] tw-border-solid tw-border-brand-navy",
    "tw-bg-brand-navy tw-text-white tw-shadow-soft",
    "enabled:hover:tw-bg-[#1A2244] enabled:hover:tw-border-[#545F8F]",
    "[&:not(:disabled):not([aria-disabled=true])]:hover:tw-bg-[#1A2244]",
    "[&:not(:disabled):not([aria-disabled=true])]:hover:tw-border-[#545F8F]",
    "[&:not(:disabled):not([aria-disabled=true])]:hover:tw-text-white",
    "focus-visible:tw-ring-brand-navy",
    RESPONSIVE_SECONDARY,
  ].join(" "),

  // App.css:4790 .vik-button-secondary (white / grey border) — confirm dialogs
  neutral: [
    "tw-duration-base tw-border tw-border-solid tw-border-[#D0D5DD]",
    "tw-bg-surface tw-text-neutral-800",
    "enabled:hover:tw-bg-[#F8F9FC] enabled:hover:tw-border-neutral-400 enabled:hover:tw-text-ink",
    "focus-visible:tw-ring-neutral-400",
  ].join(" "),

  // App.css:3872 .secondary-button.btn-orange (outline red) + :3877 hover
  // Carries .secondary-button, so it gets the same responsive rules.
  outline: [
    "tw-duration-slow tw-border tw-border-solid tw-border-brand-red",
    "tw-bg-transparent tw-text-brand-red",
    "enabled:hover:tw-bg-brand-red enabled:hover:tw-text-white",
    "focus-visible:tw-ring-brand-red",
    RESPONSIVE_SECONDARY,
  ].join(" "),

  icon: [
    "tw-duration-base tw-border tw-border-solid tw-border-line-subtle",
    "tw-bg-surface tw-text-ink tw-shadow-card",
    "enabled:hover:tw-bg-ink enabled:hover:tw-border-ink enabled:hover:tw-text-white",
    "focus-visible:tw-ring-ink",
  ].join(" "),
  iconDanger: [
    "tw-duration-base tw-border tw-border-solid tw-border-line-subtle",
    "tw-bg-surface tw-text-ink tw-shadow-card",
    "enabled:hover:tw-bg-brand-red enabled:hover:tw-border-brand-red enabled:hover:tw-text-white",
    "focus-visible:tw-ring-brand-red",
  ].join(" "),
  iconInfo: [
    "tw-duration-base tw-border tw-border-solid tw-border-line-subtle",
    "tw-bg-surface tw-text-ink tw-shadow-card",
    "enabled:hover:tw-bg-[#3B5998] enabled:hover:tw-border-[#3B5998] enabled:hover:tw-text-white",
    "focus-visible:tw-ring-[#3B5998]",
  ].join(" "),
};

const sizes = {
  md: [
    "tw-h-[46px] tw-px-[22px] tw-text-15 tw-rounded-brand",
    "max-md:tw-py-[13px]",
    "md:max-[1024px]:tw-w-auto md:max-[1024px]:tw-min-w-[130px]",
    "md:max-[1024px]:tw-max-w-full md:max-[1024px]:tw-py-[13px]",
  ].join(" "),
  // App.css:4763 .vik-button — height 46, radius 30px, min-width 150px
  pill: "tw-h-[46px] tw-px-[22px] tw-text-15 tw-rounded-pill tw-min-w-[150px]",
  // App.css:7291 — 38x38 desktop, raised to 44x44 on touch widths
  icon: "tw-h-[44px] tw-w-[44px] md:tw-h-[38px] md:tw-w-[38px] tw-p-0 tw-rounded-md10",
};

const Button = forwardRef(function Button(
  {
    as: As = "button",
    variant = "primary",
    size,
    className = "",
    type = "button",
    loading = false,
    loadingText = "Please wait...",
    disabled = false,
    children,
    ...rest
  },
  ref,
) {
  const resolvedSize = size || (variant.startsWith("icon") ? "icon" : "md");
  const cls = [base, variants[variant], sizes[resolvedSize], className]
    .filter(Boolean)
    .join(" ");
  const isNativeButton = As === "button";
  const stateProps = isNativeButton
    ? { type, disabled: disabled || loading }
    : { "aria-disabled": disabled || loading || undefined };
  return (
    <As
      ref={ref}
      className={cls}
      {...stateProps}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <>
          <SpinnerComponent /> {loadingText}
        </>
      ) : (
        children
      )}
    </As>
  );
});

export default Button;
